import { UserRole, WidgetDataParams, WidgetName } from "../types"
import {
  getDateFilterByMaxLimit,
  getOrgActiveWidgetDynamicFilters,
  getOrgFiltersMap
} from "../helpers/filters"
import {
  CONTENT_WIDGETS_ADDITIONAL_FILTERS,
  CONTENT_WIDGETS_BY_FILTER,
  CONTENT_WIDGETS_BY_VIEW,
  CONTENT_WIDGETS_FILTER_MAP,
  CONTENT_WIDGETS_REQUEST_PARAMS
} from "./widgets/content"
import {
  INSIGHTS_WIDGETS_BY_VIEW,
  INSIGHTS_WIDGETS_FILTER_MAP,
  INSIGHTS_WIDGETS_REQUEST_PARAMS
} from "./widgets/insights"
import { FiltersState } from "../states/filters"
import { getWorkkeysReplaceTerm } from "../helpers/workkeysReplaceTerms"
import { Roles } from "../../mp-utils/generic-utils"

export const WIDGET_PARAMS: Partial<Record<WidgetName, WidgetDataParams>> = {
  ActiveStates: {
    type: "org",
    label: "Active State",
    entity: "states",
    path: "/reporting/organization/states?userStatus=true",
    allowedFilters: [
      "organization",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "searchText"
    ],
    fields: {
      totalCount: "total",
      activeCount: "active"
    }
  },
  ActiveOrganizations: {
    type: "org",
    label: "Active Organization",
    entity: "organizations",
    allowedFilters: ["state", "searchText"],
    fields: {
      totalCount: "total",
      activeCount: "active"
    }
  },
  ActiveOrganizationAdmins: {
    type: "org",
    label: "Active Organization Admin",
    entity: "organizationAdmins",
    path: `/reporting/organization/admins?userStatus=true&adminRole=${UserRole.actMultiDistrictAdmin}`,
    allowedFilters: [
      "organization",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "userStatus",
      "searchText"
    ],
    excludedRoles: ["District Admin", "School Admin"],
    fields: {
      totalCount: "count",
      activeCount: "activeCount"
    }
  },
  ActiveDistrictAdmins: {
    type: "org",
    label: "Active District Admin",
    entity: "districtAdmins",
    path: `/reporting/organization/admins?userStatus=true&adminRole=${UserRole.districtAdmin}`,
    allowedFilters: [
      "organization",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "userStatus",
      "searchText"
    ],
    excludedRoles: ["School Admin"],
    fields: {
      totalCount: "count",
      activeCount: "activeCount"
    }
  },
  ActiveSchoolAdmins: {
    type: "org",
    label: `Active ${getWorkkeysReplaceTerm("school admin", true)}`,
    entity: "schoolAdmins",
    path: `/reporting/organization/admins?userStatus=true&adminRole=${UserRole.schoolAdmin}`,
    allowedFilters: [
      "organization",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "userStatus",
      "searchText"
    ],
    fields: {
      totalCount: "count",
      activeCount: "activeCount"
    }
  },
  ActiveStudents: {
    type: "org",
    label: `Active ${getWorkkeysReplaceTerm("student", true)}`,
    entity: "students",
    path: "/reporting/organization/learners?userStatus=true",
    allowedFilters: [
      "organization",
      "state",
      "district",
      "school",
      "grade",
      "teacher",
      "class",
      "userStatus",
      "student",
      "searchText"
    ],
    dynamicFilters: ["date", "userStatus"],
    requests: [
      {
        measures: ["org_view_students.activeStudentsCount"]
      },
      {
        measures: ["org_view_students.count"]
      }
    ],
    fields: {
      activeCount: "org_view_students.activeStudentsCount",
      totalCount: "org_view_students.count",
      date: "org_view_students.createdAt"
    },
    getDynamicFilters: getOrgActiveWidgetDynamicFilters("org_view_students")
  },
  ActiveTeachers: {
    type: "org",
    label: `Active ${getWorkkeysReplaceTerm("teacher", true)}`,
    entity: "teachers",
    path: "/reporting/organization/instructors?userStatus=true",
    allowedFilters: [
      "organization",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "searchText"
    ],
    dynamicFilters: ["date", "userStatus"],
    excludedRoles: ["Teacher"],
    requests: [
      {
        measures: ["org_view_teachers.activeCount"]
      },
      {
        measures: ["org_view_teachers.count"]
      }
    ],
    fields: {
      activeCount: "org_view_teachers.activeCount",
      totalCount: "org_view_teachers.count",
      date: "org_view_teachers.createdAt"
    },
    getDynamicFilters: getOrgActiveWidgetDynamicFilters("org_view_teachers")
  },
  ActiveClasses: {
    type: "org",
    label: "Active Class",
    entity: "classes",
    path: "/reporting/organization/classes?userStatus=true",
    allowedFilters: [
      "organization",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "searchText"
    ],
    dynamicFilters: ["date", "userStatus"],
    requests: [
      {
        measures: ["org_view_classes.activeCount"]
      },
      {
        measures: ["org_view_classes.count"]
      }
    ],
    fields: {
      activeCount: "org_view_classes.activeCount",
      totalCount: "org_view_classes.count",
      date: "org_view_classes.createdAt"
    },
    getDynamicFilters: getOrgActiveWidgetDynamicFilters("org_view_classes")
  },
  ActiveSchools: {
    type: "org",
    label: `Active ${getWorkkeysReplaceTerm("school", true)}`,
    entity: "schools",
    path: "/reporting/organization/institutions?userStatus=true",
    allowedFilters: [
      "organization",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "searchText"
    ],
    dynamicFilters: ["date", "userStatus"],
    requests: [
      {
        measures: ["org_view_schools.activeCount"]
      },
      {
        measures: ["org_view_schools.count"]
      }
    ],
    fields: {
      activeCount: "org_view_schools.activeCount",
      totalCount: "org_view_schools.count",
      date: "org_view_schools.createdAt"
    },
    getDynamicFilters: getOrgActiveWidgetDynamicFilters("org_view_schools")
  },
  ActiveDistricts: {
    type: "org",
    label: "Active District",
    entity: "districts",
    path: "/reporting/organization/districts?userStatus=true",
    allowedFilters: [
      "organization",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "searchText"
    ],
    dynamicFilters: ["date", "userStatus"],
    requests: [
      {
        measures: ["org_view_districts.activeCount"]
      },
      {
        measures: ["org_view_districts.count"]
      }
    ],
    fields: {
      activeCount: "org_view_districts.activeCount",
      totalCount: "org_view_districts.count",
      date: "org_view_districts.createdAt"
    },
    getDynamicFilters: getOrgActiveWidgetDynamicFilters("org_view_districts")
  },
  AverageStudentTimeSpent: {
    type: "ats",
    entity: "students",
    path: "/reporting/insights/usage/learners-usage",
    allowedFilters: [
      "test",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "grade",
      "usageContentType"
    ],
    requests: [
      {
        measures: ["avegare_time_spent_view.avgTimeSpent"],
        timeDimensions: [
          {
            dimension: "avegare_time_spent_view.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      date: "avegare_time_spent_view.createdAt",
      averageTimeSpent: "avegare_time_spent_view.avgTimeSpent"
    }
  },
  AverageDaysActive: {
    type: "ada",
    entity: "teachers",
    path: "/reporting/insights/usage/instructors-usage",
    allowedFilters: [
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "usageContentType"
    ],
    requests: [
      {
        measures: [
          "org_view_teachers.count",
          "org_view_teachers.averageDaysActive"
        ]
      }
    ],
    fields: {
      count: "org_view_teachers.count",
      averageDaysActive: "org_view_teachers.averageDaysActive",
      date: "org_view_teachers.createdAt"
    },
    getDynamicFilters: (_requestIndex: number, filters: FiltersState) => {
      if (!!filters?.date) {
        return getDateFilterByMaxLimit(
          filters.date,
          "org_view_teachers.createdAt"
        )
      }

      return []
    }
  },
  LicenseAssignment: {
    type: "licenseAssignment",
    label: "License Assignment",
    entity: "licenses",
    path: null,
    allowedFilters: ["district", "school", "date"],
    requests: [
      {
        // This will need to be replaced with actual GraphQL query
        measures: [
          "license_statistics.availableCount",
          "license_statistics.totalCount"
        ]
      }
    ],
    fields: {
      availableCount: "license_statistics.availableCount",
      totalCount: "license_statistics.totalCount"
    }
  },
  LicenseStudentCount: {
    type: "licenseStudentCount",
    label: `${getWorkkeysReplaceTerm("student", true)} License Status`,
    entity: "students",
    path: null,
    allowedFilters: [
      "district",
      "school",
      "teacher",
      "class",
      "grade",
      "licenseStatus",
      "date"
    ],
    requests: [
      {
        // Uses the same GraphQL query as LicenseAssignment
        measures: [
          "license_statistics.totalStudents",
          "license_statistics.licensedStudents"
        ]
      }
    ],
    fields: {
      totalStudents: "license_statistics.totalStudents",
      licensedStudents: "license_statistics.licensedStudents"
    },
    displayConfig: {
      entity: "Students",
      tooltip: "Total number of students and how many have active licenses.",
      footerLabel: "Licensed Students"
    }
  },
  ...INSIGHTS_WIDGETS_REQUEST_PARAMS,
  ...CONTENT_WIDGETS_REQUEST_PARAMS
}

export const WIDGETS_BY_VIEW: Record<string, Record<string, WidgetName[]>> = {
  organization: {
    organizations: [
      "ActiveOrganizations",
      "ActiveDistricts",
      "ActiveSchools",
      "ActiveTeachers",
      "ActiveStudents"
    ],
    organizations_id: [],
    states: [
      "ActiveStates",
      "ActiveOrganizations",
      "ActiveDistricts",
      "ActiveSchools",
      "ActiveTeachers",
      "ActiveStudents"
    ],
    districts: [
      "ActiveDistricts",
      "ActiveSchools",
      "ActiveTeachers",
      "ActiveStudents"
    ],
    districts_id: ["AverageStudentTimeSpent"],
    teachers_id: ["AverageDaysActive"],
    schools: ["ActiveSchools", "ActiveTeachers", "ActiveStudents"],
    admins: [
      "ActiveOrganizationAdmins",
      "ActiveDistrictAdmins",
      "ActiveSchoolAdmins"
    ],
    teachers: ["ActiveTeachers", "ActiveClasses", "ActiveStudents"],
    classes: ["ActiveClasses", "ActiveTeachers", "ActiveStudents"],
    students: ["ActiveStudents"],
    "license-assign": ["LicenseAssignment", "LicenseStudentCount"],
    "license-pools": []
  },
  insights: INSIGHTS_WIDGETS_BY_VIEW,
  content: CONTENT_WIDGETS_BY_VIEW,
  testAdministration: {
    detail: ["ContentTypePracticeTestsAverageScore"]
  }
}

export const WIDGETS_ADDITIONAL_FILTERS: Record<
  string,
  Record<string, { name: WidgetName; requestParam: string }[]>
> = {
  organization: {
    districts_id: [
      {
        name: "AverageStudentTimeSpent",
        requestParam: "avegare_time_spent_view.districtId"
      }
    ],
    teachers_id: [
      { name: "AverageDaysActive", requestParam: "org_view_teachers.teacherId" }
    ]
  },
  content: CONTENT_WIDGETS_ADDITIONAL_FILTERS
}

export const WIDGETS_BY_FILTER: Record<string, Record<string, WidgetName[]>> = {
  contentType: CONTENT_WIDGETS_BY_FILTER
}

export const WIDGETS_FILTER_MAP: Partial<
  Record<WidgetName, Record<string, string>>
> = {
  ActiveStudents: getOrgFiltersMap("org_view_students"),
  ActiveClasses: getOrgFiltersMap("org_view_classes"),
  ActiveTeachers: getOrgFiltersMap("org_view_teachers"),
  ActiveSchools: getOrgFiltersMap("org_view_schools"),
  ActiveDistricts: getOrgFiltersMap("org_view_districts"),
  AverageStudentTimeSpent: {
    ...getOrgFiltersMap("avegare_time_spent_view"),
    "tests.program": "avegare_time_spent_view.program",
    "tests.subject": "avegare_time_spent_view.subject",
    "content.type": "avegare_time_spent_view.contentType"
  },
  AverageDaysActive: {
    ...getOrgFiltersMap("org_view_teachers"),
    "content.type": "org_view_teachers.contentType"
  },
  ...INSIGHTS_WIDGETS_FILTER_MAP,
  ...CONTENT_WIDGETS_FILTER_MAP
}
